import type { Control } from '@@/packages/shared/types/models/control';
import type { Domain } from '@@/packages/shared/types/models/domain';

/**
 * Domain IDs required by a framework config: selected add-on domains, plus every
 * non-add-on domain with at least one control at one of `levels`. An empty
 * `levels` requires all non-add-on domains (the "no level set" case).
 */
export function deriveRequiredDomainIDs(
  controls: Control[],
  domains: Domain[],
  levels: string[],
  addOnDomains: string[],
): string[] {
  return domains
    .filter((d) => {
      if (d.isAddOn) return addOnDomains.includes(d.domainID);
      if (!levels.length) return true;
      return controls.some(
        (c) => c.domainID === d.domainID && c.levels.some((l) => levels.includes(l)),
      );
    })
    .map((d) => d.domainID);
}

/**
 * Control IDs required by a framework config: every control in a selected add-on
 * domain, plus every non-add-on control at one of `levels`. An empty `levels`
 * requires all non-add-on controls. Pure, so it resolves both a saved config and
 * a prospective (unsaved) selection from the same static /framework data.
 */
export function deriveRequiredControlIDs(
  controls: Control[],
  domains: Domain[],
  levels: string[],
  addOnDomains: string[],
): string[] {
  const requiredDomainIDs = deriveRequiredDomainIDs(controls, domains, levels, addOnDomains);
  const addOnDomainIDs = new Set(domains.filter((d) => d.isAddOn).map((d) => d.domainID));

  return controls
    .filter((c) => {
      if (!requiredDomainIDs.includes(c.domainID)) return false;
      if (addOnDomainIDs.has(c.domainID)) return true;
      if (!levels.length) return true;
      return c.levels.some((l) => levels.includes(l));
    })
    .map((c) => c.controlID);
}
